import React, { useState, useEffect, useCallback } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const AdminDepartments = () => {
  const { t } = useLanguage();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');
  const [selectedQrDept, setSelectedQrDept] = useState(null);

  // Edit Modal State
  const [editingDept, setEditingDept] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCode, setEditCode] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editMsg, setEditMsg] = useState('');

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await api.get('/departments');
      setDepartments(res.data);
    } catch (err) {
      console.error('Error loading departments:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMsg('');

    try {
      await api.post('/departments', { name, code });
      setName('');
      setCode('');
      setMsg(`${t('createDepartmentTitle')} - Success!`);
      fetchDepartments();
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to create department.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEdit = (dept) => {
    setEditingDept(dept);
    setEditName(dept.name);
    setEditCode(dept.code);
    setEditMsg('');
  };

  const handleUpdateDepartment = async (e) => {
    e.preventDefault();
    if (!editingDept) return;
    setEditSubmitting(true);
    setEditMsg('');

    try {
      await api.put(`/departments/${editingDept.id}`, {
        name: editName,
        code: editCode
      });
      setMsg(`Department "${editName}" updated successfully!`);
      setEditingDept(null);
      fetchDepartments();
    } catch (err) {
      setEditMsg(err.response?.data?.message || 'Failed to update department.');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('deleteConfirm'))) return;
    try {
      await api.delete(`/departments/${id}`);
      fetchDepartments();
    } catch (err) {
      alert('Failed to delete department.');
    }
  };

  const getPublicDeptUrl = (deptId) => {
    return `${window.location.origin}/public-complaint?department_id=${deptId}`;
  };

  return (
    <div className="container py-4">
      <div className="mb-4">
        <h2 className="brand-font fw-bold mb-1">{t('departments')}</h2>
        <p className="text-muted mb-0">Create, edit, and manage organizational departments and generate public QR complaint codes</p>
      </div>

      {msg && <div className="alert alert-info py-2 small mb-4 alert-dismissible fade show">{msg}</div>}

      <div className="row g-4">
        {/* Create Department Card */}
        <div className="col-lg-4">
          <div className="card glass-card border-0 p-4 sticky-top" style={{ top: '90px' }}>
            <h5 className="brand-font fw-bold mb-3 d-flex align-items-center">
              <i className="bi bi-building text-primary me-2"></i>
              {t('createDepartmentTitle')}
            </h5>
            <form onSubmit={handleCreateDepartment}>
              <div className="mb-3">
                <label className="form-label fw-semibold small">{t('departmentName')} *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Legal & Compliance"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="mb-4">
                <label className="form-label fw-semibold small">{t('departmentCode')} (Short) *</label>
                <input
                  type="text"
                  className="form-control text-uppercase"
                  placeholder="e.g. LGL"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary-gradient w-100 py-2.5" disabled={submitting}>
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-circle me-1"></i>
                    {t('createDepartmentTitle')}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Departments List Table */}
        <div className="col-lg-8">
          <div className="card glass-card border-0 p-4">
            <h5 className="brand-font fw-bold mb-3 d-flex align-items-center">
              <i className="bi bi-list-nested text-primary me-2"></i>
              {t('departments')} Directory ({departments.length})
            </h5>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"></div>
              </div>
            ) : departments.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-building-x fs-1 d-block mb-2 text-muted"></i>
                <p className="mb-0">No departments configured yet.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>{t('departmentCode')}</th>
                      <th>{t('departmentName')}</th>
                      <th>{t('dateGenerated')}</th>
                      <th className="text-end">{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departments.map((d) => (
                      <tr key={d.id}>
                        <td>#{d.id}</td>
                        <td>
                          <span className="badge bg-primary font-monospace px-2.5 py-1.5">{d.code}</span>
                        </td>
                        <td className="fw-semibold text-dark">{d.name}</td>
                        <td className="text-muted small">
                          {new Date(d.created_at).toLocaleDateString()}
                        </td>
                        <td className="text-end">
                          <div className="btn-group btn-group-sm">
                            <button
                              onClick={() => handleOpenEdit(d)}
                              className="btn btn-outline-primary"
                              title={t('editDepartmentTitle')}
                            >
                              <i className="bi bi-pencil-square me-1"></i> {t('edit')}
                            </button>
                            <button
                              onClick={() => setSelectedQrDept(d)}
                              className="btn btn-outline-success"
                              title="Generate QR Code"
                            >
                              <i className="bi bi-qr-code me-1"></i> QR
                            </button>
                            <button
                              onClick={() => handleDelete(d.id)}
                              className="btn btn-outline-danger"
                              title="Delete Department"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* EDIT DEPARTMENT MODAL */}
      {editingDept && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0 rounded-4">
              <div className="modal-header border-bottom bg-light">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-pencil-square text-primary me-2"></i>
                  {t('editDepartmentTitle')}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEditingDept(null)}
                  aria-label="Close"
                ></button>
              </div>
              <form onSubmit={handleUpdateDepartment}>
                <div className="modal-body p-4">
                  {editMsg && <div className="alert alert-danger py-2 small mb-3">{editMsg}</div>}

                  <div className="mb-3">
                    <label className="form-label fw-semibold small">{t('departmentName')} *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small">{t('departmentCode')} *</label>
                    <input
                      type="text"
                      className="form-control text-uppercase"
                      value={editCode}
                      maxLength={6}
                      onChange={(e) => setEditCode(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="modal-footer border-top bg-light">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setEditingDept(null)}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary-gradient px-4"
                    disabled={editSubmitting}
                  >
                    {editSubmitting ? 'Saving...' : t('updateDepartmentBtn')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Poster Modal */}
      {selectedQrDept && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1050 }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-qr-code-scan me-2"></i> Department QR Poster
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setSelectedQrDept(null)}
                ></button>
              </div>
              <div className="modal-body text-center p-4">
                <div className="border border-3 border-primary p-4 rounded-3 bg-light shadow-sm">
                  <span className="badge bg-primary fs-6 px-3 py-2 text-uppercase mb-2">{selectedQrDept.code} Department</span>
                  <h3 className="fw-bold text-dark">{selectedQrDept.name}</h3>
                  <p className="text-muted small mb-3">Scan to open public complaint desk for this department</p>

                  <div className="bg-white p-3 d-inline-block rounded border shadow-sm mb-3">
                    <QRCodeCanvas
                      value={getPublicDeptUrl(selectedQrDept.id)}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </div>

                  <p className="fw-bold text-secondary mb-1">Scan with camera to report issues to {selectedQrDept.name}</p>
                </div>

                <div className="mt-3 text-start small">
                  <strong className="text-muted">Target Link:</strong>
                  <input type="text" className="form-control form-control-sm mt-1" readOnly value={getPublicDeptUrl(selectedQrDept.id)} />
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setSelectedQrDept(null)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary fw-bold"
                  onClick={() => window.print()}
                >
                  <i className="bi bi-printer-fill me-1"></i> Print Poster
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDepartments;
